import React from 'react';
import { ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { VENTURES } from '../data/mock';

export default function Ventures() {
  return (
    <section id="ventures" className="relative py-24 lg:py-32 bg-[#FDF8F2]">
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] rounded-full bg-[radial-gradient(closest-side,#FFE0C4,transparent_70%)] opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">
              Our Ventures
            </span>
            <h2
              className="mt-3 text-[36px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.025em] text-[#0F1B2D] font-semibold max-w-3xl"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Two flagship products. One mission — accessible, evidence-based MedTech.
            </h2>
          </div>
          <p className="text-[15.5px] text-slate-600 max-w-md leading-relaxed">
            Mednosys backs, builds, and scales deep-tech healthcare products from clinical insight to hospital deployment.
          </p>
        </div>

        <div className="grid gap-8 lg:gap-10">
          {VENTURES.map((v, idx) => (
            <article
              key={v.id}
              className="group relative rounded-3xl bg-white border border-orange-100/80 overflow-hidden shadow-[0_20px_60px_-30px_rgba(15,27,45,0.15)] hover:shadow-[0_30px_80px_-30px_rgba(232,90,24,0.35)] transition-shadow duration-500"
            >
              <div className={`grid lg:grid-cols-5 ${idx % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                {/* Image */}
                <div className="relative lg:col-span-2 h-64 lg:h-auto overflow-hidden">
                  <img
                    src={v.image}
                    alt={v.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FF7A2E]/25 via-transparent to-[#0F1B2D]/40 mix-blend-multiply" />
                  <div className="absolute top-5 left-5 inline-flex items-center rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#E85A18]">
                    {v.tag}
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-3 p-8 lg:p-12 flex flex-col">
                  <h3
                    className="text-[32px] lg:text-[40px] leading-tight tracking-tight text-[#0F1B2D] font-semibold"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {v.name}
                  </h3>
                  <p className="mt-2 text-[16px] text-[#E85A18] font-medium">{v.tagline}</p>

                  {v.approvals && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {v.approvals.map((a) => (
                        <span
                          key={a}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] font-semibold tracking-wide ${
                            a.includes('CDSCO')
                              ? 'bg-gradient-to-r from-[#FFB27A] to-[#E85A18] text-white shadow-[0_6px_18px_-8px_rgba(232,90,24,0.6)]'
                              : 'bg-[#FFF6EE] text-[#B54A15] border border-orange-200'
                          }`}
                        >
                          {a.includes('CDSCO') && <span className="h-1.5 w-1.5 rounded-full bg-white/90" />}
                          {a}
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="mt-4 text-[15px] leading-[1.7] text-slate-600 max-w-2xl">
                    {v.description}
                  </p>

                  <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {v.highlights.map((h) => (
                      <div key={h} className="flex items-start gap-2 text-[14px] text-slate-700">
                        <CheckCircle2 className="h-4 w-4 text-[#FF7A2E] mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 grid grid-cols-3 gap-4 border-t border-orange-100 pt-6">
                    {v.metrics.map((m) => (
                      <div key={m.label}>
                        <div
                          className="text-[24px] lg:text-[28px] font-semibold text-[#0F1B2D]"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {m.value}
                        </div>
                        <div className="text-[11.5px] uppercase tracking-[0.14em] text-slate-500 mt-1">
                          {m.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8">
                    <a
                      href={v.link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-[#0F1B2D] hover:bg-[#1a2942] text-white px-5 py-2.5 text-[13.5px] font-medium transition-colors"
                    >
                      Visit {v.name}
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
